import React, { useState } from 'react';
import { useCampaignForm } from '../context/CampaignFormContext';
import { CopyRefinementModal } from './CopyRefinementModal';

interface CopySuggestion {
  id: string;
  headline: string;
  description: string;
  cta: string;
  score: number;
  tone: string;
}

export const AICopyGeneratorEnhanced: React.FC = () => {
  const { applyCopy } = useCampaignForm();
  const [isGenerating, setIsGenerating] = useState(false);
  const [suggestions, setSuggestions] = useState<CopySuggestion[]>([]);
  const [productName, setProductName] = useState('');
  const [audience, setAudience] = useState('');
  const [objective, setObjective] = useState('conversions');
  const [refinementModalOpen, setRefinementModalOpen] = useState(false);
  const [selectedSuggestion, setSelectedSuggestion] = useState<CopySuggestion | null>(null);

  const handleGenerate = async () => {
    setIsGenerating(true);
    
    try {
      // Call real OpenAI API via backend
      const response = await fetch('http://localhost:8000/api/v1/ai-copy/generate', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          product_name: productName || 'Your Product',
          audience: audience || 'General Audience',
          objective: objective,
          tone: 'friendly',
          platform: 'meta',
          count: 10
        })
      });

      if (!response.ok) {
        throw new Error(`API error: ${response.status}`);
      }

      const data = await response.json();
      
      // Transform API response to match our interface
      const apiSuggestions: CopySuggestion[] = data.suggestions.map((s: any) => ({
        id: s.id,
        headline: s.headline,
        description: s.description,
        cta: s.cta,
        score: s.predicted_score || 85,
        tone: s.tone || 'friendly'
      }));

      setSuggestions(apiSuggestions);
      setIsGenerating(false);
      
    } catch (error) {
      console.error('Failed to generate AI copy:', error);
      setIsGenerating(false);
      
      // Fallback to mock data if API fails
      alert('⚠️ OpenAI API unavailable. Using mock data. Error: ' + (error as Error).message);
      
      const mockSuggestions: CopySuggestion[] = [
      {
        id: '1',
        headline: `${productName || 'Your Product'} - Limited Time!`,
        description: `Get ${productName || 'amazing deals'} today. ${audience || 'Everyone'} loves it!`,
        cta: 'Shop Now',
        score: 94,
        tone: 'urgent'
      },
      {
        id: '2',
        headline: `Transform Your Life with ${productName || 'Our Solution'}`,
        description: `Join thousands of satisfied customers. Perfect for ${audience || 'you'}.`,
        cta: 'Learn More',
        score: 92,
        tone: 'inspiring'
      },
      {
        id: '3',
        headline: `${productName || 'Quality Products'} You Can Trust`,
        description: `Premium quality at unbeatable prices. Ideal for ${audience || 'everyone'}.`,
        cta: 'Get Started',
        score: 89,
        tone: 'professional'
      },
      {
        id: '4',
        headline: `Don't Miss Out: ${productName || 'Special Offer'}`,
        description: `Exclusive deal for ${audience || 'you'}. Limited quantities available!`,
        cta: 'Claim Now',
        score: 87,
        tone: 'urgent'
      },
      {
        id: '5',
        headline: `Discover ${productName || 'Amazing Benefits'}`,
        description: `See why ${audience || 'thousands'} are choosing us. Try risk-free today!`,
        cta: 'Discover More',
        score: 85,
        tone: 'informative'
      },
      {
        id: '6',
        headline: `${productName || 'Premium Quality'} at Your Fingertips`,
        description: `Designed specifically for ${audience || 'people like you'}. Order now!`,
        cta: 'Order Today',
        score: 83,
        tone: 'professional'
      },
      {
        id: '7',
        headline: `Experience ${productName || 'The Difference'}`,
        description: `Join ${audience || 'smart shoppers'} who've already made the switch.`,
        cta: 'Try Now',
        score: 81,
        tone: 'casual'
      },
      {
        id: '8',
        headline: `${productName || 'Best Deal'} of the Season`,
        description: `Perfect for ${audience || 'you'}. Don't wait - offer ends soon!`,
        cta: 'Buy Now',
        score: 79,
        tone: 'urgent'
      },
      {
        id: '9',
        headline: `Why ${audience || 'Everyone'} Loves ${productName || 'Us'}`,
        description: `Trusted by thousands. See the results for yourself today.`,
        cta: 'See Why',
        score: 77,
        tone: 'testimonial'
      },
      {
        id: '10',
        headline: `${productName || 'Your Solution'} Starts Here`,
        description: `Made for ${audience || 'you'}. Get started in minutes, no hassle.`,
        cta: 'Start Now',
        score: 75,
        tone: 'friendly'
      }
    ];
    
    setSuggestions(mockSuggestions);
      setIsGenerating(false);
    }
  };

  const handleApply = (suggestion: CopySuggestion) => {
    applyCopy({
      headline: suggestion.headline,
      description: suggestion.description,
      cta: suggestion.cta
    }, suggestion.id);
    
    alert('✅ Copy applied successfully!');
  };

  const handleRefine = (suggestion: CopySuggestion) => {
    setSelectedSuggestion(suggestion);
    setRefinementModalOpen(true);
  };

  const handleRefinedApply = (copy: { headline: string; description: string; cta: string }) => {
    applyCopy(copy, selectedSuggestion?.id);
    alert('✅ Refined copy applied successfully!');
  };

  return (
    <div className="space-y-6">
      <CopyRefinementModal
        isOpen={refinementModalOpen}
        onClose={() => setRefinementModalOpen(false)}
        onApply={handleRefinedApply}
        initialCopy={selectedSuggestion || { headline: '', description: '', cta: '' }}
      />
      <div className="bg-white dark:bg-boxdark rounded-lg p-6 border border-stroke">
        <h3 className="text-xl font-semibold mb-4">✨ AI Copy Generator</h3>

        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium mb-2">Product/Service Name</label>
            <input
              type="text"
              value={productName}
              onChange={(e) => setProductName(e.target.value)}
              placeholder="e.g., EcoWater Bottles"
              className="w-full px-4 py-2 border border-stroke rounded-lg dark:bg-boxdark"
            />
          </div>

          <div>
            <label className="block text-sm font-medium mb-2">Target Audience</label>
            <input
              type="text"
              value={audience}
              onChange={(e) => setAudience(e.target.value)}
              placeholder="e.g., 25-34 eco-conscious women"
              className="w-full px-4 py-2 border border-stroke rounded-lg dark:bg-boxdark"
            />
          </div>

          <div>
            <label className="block text-sm font-medium mb-2">Campaign Objective</label>
            <select
              value={objective}
              onChange={(e) => setObjective(e.target.value)}
              className="w-full px-4 py-2 border border-stroke rounded-lg dark:bg-boxdark"
            >
              <option value="conversions">Drive Conversions</option>
              <option value="awareness">Build Awareness</option>
              <option value="leads">Generate Leads</option>
              <option value="traffic">Increase Traffic</option>
            </select>
          </div>

          <button
            onClick={handleGenerate}
            disabled={isGenerating}
            className="w-full px-6 py-3 bg-primary text-white rounded-lg hover:bg-opacity-90 disabled:opacity-50 font-medium"
          >
            {isGenerating ? '🤖 Generating...' : '✨ Generate 10 Variations'}
          </button>
        </div>
      </div>

      {suggestions.length > 0 && (
        <div className="space-y-3">
          <h4 className="font-semibold text-lg">AI Suggestions (Ranked by Score)</h4>
          
          {suggestions.map((suggestion, index) => (
            <div
              key={suggestion.id}
              className="bg-white dark:bg-boxdark rounded-lg p-5 border border-stroke hover:border-primary transition-colors"
            >
              <div className="flex justify-between items-start mb-3">
                <div className="flex items-center gap-3">
                  <span className="text-2xl font-bold text-gray-400">#{index + 1}</span>
                  <span className={`px-3 py-1 rounded-full text-sm font-medium ${
                    suggestion.score >= 90 ? 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200' :
                    suggestion.score >= 80 ? 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-200' :
                    'bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-300'
                  }`}>
                    Score: {suggestion.score}
                  </span>
                  <span className="px-3 py-1 bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200 rounded-full text-sm">
                    {suggestion.tone}
                  </span>
                </div>

                <div className="flex gap-2">
                  <button
                    onClick={() => handleRefine(suggestion)}
                    className="px-4 py-2 border border-stroke rounded-lg hover:bg-gray-50 dark:hover:bg-meta-4 font-medium"
                  >
                    ✏️ Refine
                  </button>
                  <button
                    onClick={() => handleApply(suggestion)}
                    className="px-4 py-2 bg-primary text-white rounded-lg hover:bg-opacity-90 font-medium"
                  >
                    ✓ Apply
                  </button>
                </div>
              </div>

              <div className="space-y-2">
                <p className="font-semibold text-lg">
                  <span className="text-gray-500 text-sm">Headline:</span> {suggestion.headline}
                </p>
                <p className="text-gray-700 dark:text-gray-300">
                  <span className="text-gray-500 text-sm">Description:</span> {suggestion.description}
                </p>
                <p className="text-primary font-medium">
                  <span className="text-gray-500 text-sm">CTA:</span> {suggestion.cta}
                </p>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};
